
import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import ModernRoundRobinGroupsTable from './ModernRoundRobinGroupsTable';
import RoundRobinGroupModal from './RoundRobinGroupModal';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { getRoundRobinGroups, deleteRoundRobinGroup, type RoundRobinGroup } from '@/actions/round-robin-groups';

const RoundRobinGroupsPage = () => {
  const [groups, setGroups] = useState<RoundRobinGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RoundRobinGroup | null>(null);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const response = await getRoundRobinGroups();
      if (response.success && response.data) {
        setGroups(response.data);
      }
    } catch (error) {
      console.error('Error fetching round robin groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setIsModalOpen(true);
  };

  const handleEditGroup = (group: RoundRobinGroup) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  };

  const handleDeleteGroup = async (group: RoundRobinGroup) => {
    if (confirm('Are you sure you want to delete this round robin group?')) {
      const response = await deleteRoundRobinGroup(group.group_id);
      if (response.success) {
        fetchGroups();
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
  };

  const handleModalSuccess = () => {
    fetchGroups();
    handleModalClose();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10">
        <ModernSettingHeader
          title="Round Robin Groups"
          description="Create and manage round robin groups for automatic lead distribution based on field values"
          icon={<Users className="w-6 h-6" />}
          buttonText="New Round Robin Group"
          onClick={handleCreateGroup}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <ModernRoundRobinGroupsTable
          data={groups}
          onEdit={handleEditGroup}
          onDelete={handleDeleteGroup}
          isLoading={isLoading}
        />
      </div>

      {/* Modal */}
      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={editingGroup ? 'Edit Round Robin Group' : 'Create New Round Robin Group'}
        subtitle={editingGroup ? 'Modify group settings and member distribution rules' : 'Set up automatic lead distribution based on field values'}
        icon={<Users className="w-6 h-6" />}
      >
        <RoundRobinGroupModal
          group={editingGroup}
          onSuccess={handleModalSuccess}
          onClose={handleModalClose}
        />
      </SettingsSlideModal>
    </div>
  );
};

export default RoundRobinGroupsPage;